// services/api.js

const API_BASE_URL = "https://blog.cloudvantage.com.ng/api";

// ============================================
// TOKEN AND ADMIN MANAGEMENT
// ============================================
const getToken = () => localStorage.getItem("adminToken");
const setToken = (token) => localStorage.setItem("adminToken", token);
const removeToken = () => localStorage.removeItem("adminToken");

const setAdmin = (admin) =>
  localStorage.setItem("adminData", JSON.stringify(admin));
const getAdmin = () => {
  const admin = localStorage.getItem("adminData");
  return admin ? JSON.parse(admin) : null;
};
const removeAdmin = () => localStorage.removeItem("adminData");

// ============================================
// TOKEN EXPIRATION CHECKING
// ============================================
/**
 * Check if JWT token is expired without verifying signature
 * @returns {boolean} true if token exists and is not expired, false otherwise
 */
const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;

  try {
    // JWT payload is base64url encoded, decode it
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token has expired
    if (payload.exp && payload.exp < currentTime) {
      return true; // Token is expired
    }
    return false; // Token is valid
  } catch (error) {
    // If we can't parse the token, consider it invalid/expired
    return true;
  }
};

/**
 * Handle API response errors, especially token expiration
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed response data
 */
const handleApiResponse = async (response) => {
  let data;
  try {
    // Try to parse JSON, but handle non-JSON responses
    const text = await response.text();
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    // If response is not JSON, create a data object from status
    data = { message: response.statusText || "Unknown error" };
  }
  
  // Check if token is expired (401 Unauthorized)
  if (response.status === 401) {
    // Clear token and admin data
    removeToken();
    removeAdmin();
    
    // Redirect to login if we're in the browser
    if (typeof window !== "undefined") {
      // Show alert if user is on an authenticated page
      if (window.location.pathname !== "/login" && window.location.pathname !== "/register") {
        alert("Your session has expired. Please login again.");
        window.location.href = "/login";
      }
    }
    
    // Throw error so calling code can handle it
    throw new Error("Session expired. Please login again.");
  }
  
  // Return data if response is ok
  if (response.ok) {
    return data;
  }
  
  // Handle other errors
  throw new Error(data.message || data.error || `API Error: ${response.statusText}`);
};

// ============================================
// AUTH API
// ============================================
export const authAPI = {
  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });
    const data = await response.json();

    // Backend returns { message, token, admin } not { success, token, admin }
    if (data.token) {
      setToken(data.token);
      setAdmin(data.admin);
      return { success: true, ...data };
    }

    // If no token, registration failed
    return { success: false, message: data.message || "Registration failed" };
  },

  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });
    const data = await response.json();

    // Backend returns { message, token, admin } not { success, token, admin }
    if (data.token) {
      setToken(data.token);
      setAdmin(data.admin);
      return { success: true, ...data };
    }

    // If no token, login failed
    return { success: false, message: data.message || "Login failed" };
  },

  logout: () => {
    removeToken();
    removeAdmin();
    return { success: true };
  },

  getProfile: async () => {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return await handleApiResponse(response);
  },

  isAuthenticated: () => {
    const token = getToken();
    if (!token) return false;
    // Check if token is expired
    return !isTokenExpired();
  },

  getCurrentAdmin: () => {
    return getAdmin();
  },
};

// ============================================
// CATEGORY API
// ============================================
export const getCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`);
    const result = await response.json();

    // If backend sends { success, data: [...] }, return result.data
    if (result && Array.isArray(result.data)) return result.data;

    // If backend sends just an array, return it
    if (Array.isArray(result)) return result;

    return [];
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

export const createCategory = async (categoryData) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(categoryData),
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error creating category:", error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
};

// ============================================
// AUTHOR API
// ============================================
export const getAuthors = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/authors`);
    const result = await response.json();

    if (result && Array.isArray(result.data)) return result.data;
    if (Array.isArray(result)) return result;

    return [];
  } catch (error) {
    console.error("Error fetching authors:", error);
    return [];
  }
};

export const createAuthor = async (authorData) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/authors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(authorData),
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error creating author:", error);
    throw error;
  }
};

export const deleteAuthor = async (id) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/authors/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error deleting author:", error);
    throw error;
  }
};

// ============================================
// BLOG API
// ============================================
export const getBlogs = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${API_BASE_URL}/blogs?${queryString}`
      : `${API_BASE_URL}/blogs`;

    const response = await fetch(url);
    const result = await response.json();

    if (result && Array.isArray(result.data)) return result.data;
    if (Array.isArray(result)) return result;

    return [];
  } catch (error) {
    console.error("Error fetching blogs:", error);
    return [];
  }
};

export const getBlogById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/blogs/${id}`);
    const result = await response.json();
    // Backend returns { success, data }
    if (result && result.data) return result.data;
    return result;
  } catch (error) {
    console.error("Error fetching blog:", error);
    throw error;
  }
};

export const getBlogBySlug = async (slug) => {
  try {
    const response = await fetch(`${API_BASE_URL}/blogs/slug/${slug}`);
    const result = await response.json();
    if (result && result.data) return result.data;
    const message = result?.message || "Failed to load blog";
    throw new Error(message);
  } catch (error) {
    console.error("Error fetching blog by slug:", error);
    throw error;
  }
};

export const createBlog = async (blogData) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const formData = new FormData();

    // Handle featured image
    if (blogData.featuredImage) {
      formData.append("featuredImage", blogData.featuredImage);
    }

    // Handle additional images
    if (blogData.images && Array.isArray(blogData.images)) {
      blogData.images.forEach((img) => {
        formData.append("images", img);
      });
    }

    // Append other fields
    Object.keys(blogData).forEach((key) => {
      if (
        key !== "featuredImage" &&
        key !== "images" &&
        blogData[key] !== null &&
        blogData[key] !== undefined
      ) {
        formData.append(key, blogData[key]);
      }
    });
    

    const response = await fetch(`${API_BASE_URL}/blogs`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error creating blog:", error);
    throw error;
  }
};

export const updateBlog = async (id, blogData) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const formData = new FormData();

    // Handle featured image
    if (blogData.featuredImage) {
      formData.append("featuredImage", blogData.featuredImage);
    }

    // Handle additional images
    if (blogData.images && Array.isArray(blogData.images)) {
      blogData.images.forEach((img) => {
        formData.append("images", img);
      });
    }

    // Append other fields
    Object.keys(blogData).forEach((key) => {
      if (
        key !== "featuredImage" &&
        key !== "images" &&
        blogData[key] !== null &&
        blogData[key] !== undefined
      ) {
        formData.append(key, blogData[key]);
      }
    });

    const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error updating blog:", error);
    throw error;
  }
};

export const deleteBlog = async (id) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error deleting blog:", error);
    throw error;
  }
};

export const togglePublishBlog = async (id) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/blogs/${id}/publish`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error toggling publish:", error);
    throw error;
  }
};

// ============================================
// ADMIN BLOGS (for dashboard)
// ============================================
export const getAdminBlogs = async () => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/blogs/admin`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await handleApiResponse(res);
    // Normalize to array
    if (result && Array.isArray(result.data)) return result.data;
    if (Array.isArray(result)) return result;
    return [];
  } catch (error) {
    console.error("Error fetching admin blogs:", error);
    throw error;
  }
};

export { API_BASE_URL };

// ============================================
// SUBSCRIBERS API
// ============================================
export const subscribeToBlog = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscribers`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });
    return await response.json();
  } catch (error) {
    console.error("Error subscribing:", error);
    return { success: false, message: error.message };
  }
};

export const getSubscribers = async () => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/subscribers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await handleApiResponse(response);
    if (Array.isArray(result?.data)) return result.data;
    return [];
  } catch (error) {
    console.error("Error fetching subscribers:", error);
    throw error;
  }
};

export const deleteSubscriber = async (id) => {
  try {
    // Check token expiration before making request
    if (isTokenExpired()) {
      removeToken();
      removeAdmin();
      throw new Error("Session expired. Please login again.");
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/subscribers/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error deleting subscriber:", error);
    throw error;
  }
};

